import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import { Modal, message, Row, Col, Table, Popconfirm, Button } from "antd";
import { edit_bank_transactions } from "../../../../services";
import { downloadXlsx } from "../../templatesConverter";

export const UploadTransactionsModal = forwardRef(({
	token,
	bankAccountSelected,	
	loading = false,
	setLoading,		
	onOpenConvertTemplate,
	onEmitNewTransactions,
	onClearFileList,
}, ref) => {
	const [ fileContent, setFileContent ] = useState({});
	const [ openUploadTransaction, setOpenUploadTransaction ] = useState(false);
	const [ previewData, setPreviewData ] = useState([]);  // Estado para almacenar los datos del preview
	const [ columns, setColumns ] = useState([]);  // Estado para las columnas de la tabla de vista previa
	const { file: fileList = [], fileData = [], selectedBank = "" } = fileContent;
	
	useImperativeHandle(ref, () => ({
		openModal: (value) => setOpenUploadTransaction(value),
		onSetFileContent: (value) => setFileContent(value),
	}));
	
	const handleFilePreview = () => {
		const jsonData = fileData;
		if(jsonData.length > 0){
			const headers = jsonData[0];
			setColumns(headers.map(header => ({title: header, dataIndex: header, key: header})));
			setPreviewData(jsonData.slice(1).map((row, index) => {
				const rowData = {};
				headers.forEach((header, idx) => {
					rowData[header] = row[idx] ?? "";
				});
				return { key: index, ...rowData };
			}));
		}
	};
	
	// Generate preview on modal open or file change
	useEffect(() => {
		if (fileList.length > 0) {
			handleFilePreview();
		}
	}, [fileList]);
	
	// Función para manejar la subida del archivo
	const handleUpload = async () => {
		if (fileList.length === 0) {
			message.error("No files selected for upload");
			return;
		}

		setLoading(true);

		const base64FileContent = await getBase64(fileList[0].originFileObj);

		const data = {
			filename: fileList[0].name,
			mimeType: fileList[0].originFileObj.type,
			fileContent: base64FileContent,
			bank_account_id: bankAccountSelected?.id,
		};

		try {			
			const response = await edit_bank_transactions(token, "upload", data);

			if (response) {
				const { data, message : messageGetted, updatedBasePoints, updatedTemporalBasePoints } = response;
				
				message.success(messageGetted);
				
				if (onClearFileList) onClearFileList();

				onEmitNewTransactions({
					newBankTransactions: data,
					updatedBasePoints,
					updatedTemporalBasePoints,
				});
				
				setPreviewData([]);
				setOpenUploadTransaction(false);
			} else {
				message.error("Error in upload response");
			}
		} catch (error) {
			console.error("Upload failed:", error);
			message.error("Upload failed");
		} finally {
			setLoading(false);
		}
	};

	// Convert file to base64
	const getBase64 = (file) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => {
				const base64Data = reader.result.split(",")[1];
				resolve(base64Data);
			};
			reader.onerror = (error) => reject(error);
		});
	};

	const downloadFile = async () => {
		downloadXlsx(fileData, selectedBank);
	};
	
	return (
		<Modal
			open={openUploadTransaction}
			centered
			width='80vw'
			title='Subir Plantilla'
			onCancel={() => {
				setOpenUploadTransaction(false);
			}}
			footer={[
				<Button
					primary
					onClick={() => {
						setOpenUploadTransaction(false);
						onOpenConvertTemplate(true);
					}}
					key={"0"}
				>
                    Regresar a Convertir Plantilla
				</Button>,
				<Button
					primary
					onClick={() => setOpenUploadTransaction(false)}
					key={"1"}
				>
                    Cancelar
				</Button>,
				<Button
					primary
					onClick={downloadFile}
					key={"2"}
				>
					Descargar Plantilla
				</Button>,
				<Popconfirm
					title={`¿Estás seguro de que esta plantilla pertenece a la cuenta bancaria ${bankAccountSelected?.bank || ""}-${bankAccountSelected?.account_number || ""}?`}
					onConfirm={handleUpload}
					okText="Sí"
					cancelText="No"
					disabled={fileList.length === 0 || loading}
					key={"3"}
				>
					<Button
						type="primary"
						disabled={fileList.length === 0 || loading}
					>
                        Subir
					</Button>
				</Popconfirm>,
			]}
		>
			<Row gutter={24}>
				<Col span={24}>
					{previewData.length > 0 && (
						<Table
							dataSource={previewData}
							columns={columns}
							pagination={false}
							scroll={{ x: "100%", y: 240 }}
						/>
					)}
				</Col>
			</Row>
		</Modal>
	);
});

UploadTransactionsModal.displayName = "UploadTransactionsModal";

UploadTransactionsModal.propTypes = {
	token: PropTypes.string.isRequired,
	bankAccountSelected: PropTypes.shape({
		id: PropTypes.string.isRequired,
		bank: PropTypes.string.isRequired,
		account_number: PropTypes.string.isRequired,
		currency: PropTypes.string.isRequired,
		balance: PropTypes.number.isRequired,
		diaries: PropTypes.object.isRequired,
	}).isRequired,
	onOpenConvertTemplate: PropTypes.func.isRequired,
	onEmitNewTransactions: PropTypes.func.isRequired,
	loading: PropTypes.bool,
	setLoading: PropTypes.func.isRequired,
	onClearFileList: PropTypes.func,
};
